import React from 'react';
import {   InfoBox,   ExerciseBox,   CodeBlock,   InlineCode,   SectionTitle, Image, CorrectionBox, Note,  TableContainer,  Result,  PatternContainer,  SectionHeading,  SubHeading,  TwoColGrid,  ThreeColGrid,  GridItem,  Divider, Separator, ColoredText} from '../../../../components/course/CourseComponents';
import repereImage from '../repere.jpg';
import distImage from '../dist.jpg';

const Chapitre2Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Ajouter un gameplay simple : dessiner un cercle, et le changer de place quand on clique dessus.
        </p>
      </InfoBox>      

      <p>Maintenant qu'on a notre boucle de jeu, on va ajouter du gameplay.</p>

      <Separator type="text" text="Afficher un cercle" color="gray-darker" size="md" />
      <SectionTitle>🎯 1/ Afficher un cercle</SectionTitle>

      <p>Commençons par afficher un cercle grâce à la fonction fournie par p5.js :</p>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>circle(x, y, diamètre)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Dessine un cercle. <br/>- Paramètre 1 : position en x<br/> - Paramètre 2 : position en y<br/> - Paramètre 3 : diamètre (taille) du cercle</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <Note type="tip" title="💡 Systeme de coordonnée dans le Canvas p5.js :">
        Comme dans un repère orthonormé, x représente l'axe horizontal, et y l'axe vertical.<br/>
        La position (0, 0) est en haut à gauche du canvas.<br/>
        (600, 600) est en bas à droite du canvas.
        
        <Image 
          src={repereImage} 
          alt="Système de coordonnées dans p5.js"
          borderRadius="xl"
          shadow={true}
          border={false}
          width="400px"
          className="mt-4"
        />
      </Note>

      <p>Pour changer la couleur du cercle, on utilise la fonction :</p>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>fill(rouge, vert, bleu)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Définit la couleur de remplissage des formes. <br/>- Paramètre 1 : Composante Rouge de 0 à 255<br/> - Paramètre 2 : Composante Verte de 0 à 255<br/> - Paramètre 3 : Composante Bleue de 0 à 255</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <p>Elle définit la couleur utilisée pour dessiner les formes suivantes. Cette couleur restera active jusqu'à ce qu'un nouveau fill() soit appelé.</p>

      <Note type="exercice" title="🧪 Afficher un cercle de couleur :">
        Dessine un cercle turquoise en ajoutant ces deux lignes dans la fonction <InlineCode>draw()</InlineCode> :
        <CodeBlock language="javascript">
{`// Cercle
fill(34, 174, 220);
circle(300, 300, 100);`}
        </CodeBlock>


<br/>
        <Result>▶️ Affichage d'un cercle turquoise au centre de la fenêtre.</Result>
        <br/> Ce cercle est affiché 60 fois par seconde grâce à la fonction draw().

        <CorrectionBox>
        <CodeBlock language="javascript" showCopyButton={true}>
{`function draw() {
  background(9, 26, 52);

  // Cercle
  fill(34, 174, 220);
  circle(300, 300, 100);
}`}
        </CodeBlock>
      </CorrectionBox>

       
      </Note>

      <Note type="tip" title="💡 Pourquoi au centre ?">
        Car la fenêtre fait 600×600 pixels, et on affiche notre cercle aux coordonnées (300, 300).
      </Note>

      <Separator type="text" text="Position aléatoire" color="gray-darker" size="md" />
      <SectionTitle>🎲 2/ Position aléatoire</SectionTitle>

      <p>Nous allons afficher ce cercle à un endroit aléatoire, en utilisant la fonction :</p>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>random(min, max)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Génère un nombre aléatoire. <br/>- Paramètre 1 : valeur minimale<br/> - Paramètre 2 : valeur maximale<br/> - Retourne : un nombre entre min et max</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <p><InlineCode>random(50, 550);</InlineCode> : choisi un nombre entier aléatoire compris entre 50 et 550</p>

      <Note type="tip" title="💡 Pourquoi ne pas choisir entre 0 et 600 ?">
        Parce qu'une partie du cercle risquerait de sortir de l'écran.<br/>
        Il faut donc laisser une marge correspondant au rayon du cercle : 50.
      </Note>

      <Note type="exercice" title="🧪 Creation des variables :">
        - Crée trois variables globales, tout en haut de ton code :
        <CodeBlock language="javascript">
{`let cercle_rayon = 50;
let cercle_x = 0;
let cercle_y = 0;`}
        </CodeBlock>
        <InlineCode>cercle_rayon</InlineCode> contient la taille du cercle, <InlineCode>cercle_x</InlineCode> et <InlineCode>cercle_y</InlineCode> contiennent les coordonnées du cercle.
        <br/><br/>
        - Dans la fonction <InlineCode>setup()</InlineCode>, choisis des valeurs aléatoires pour les coordonnées :
        <CodeBlock language="javascript">
{`cercle_x = random(50, 550);
cercle_y = random(50, 550);`}
        </CodeBlock>
        À chaque lancement du jeu, ces valeurs seront différentes.
        <br/><br/>
        - Dans la fonction <InlineCode>draw()</InlineCode>, remplace les coordonnées fixes par les variables :
        <CodeBlock language="javascript">
        <ColoredText color="red">- circle(300, 300, 100);</ColoredText>{"\n"}
        <ColoredText color="green">+ circle(cercle_x, cercle_y, cercle_rayon * 2);</ColoredText>
        </CodeBlock>
        <br/>
        <Result>▶️ A chaque lancement, ton cercle devrai changer de place !</Result>

        <CorrectionBox>
        <CodeBlock language="javascript" showCopyButton={true}>
{`// Jeu 1 : Améliore ta visée
let cercle_rayon = 50;
let cercle_x = 0;
let cercle_y = 0;

function setup() {
  createCanvas(600, 600);
  cercle_x = random(50, 550);
  cercle_y = random(50, 550);
}

function draw() {
  background(9, 26, 52);

  // Cercle
  fill(34, 174, 220);
  circle(cercle_x, cercle_y, cercle_rayon * 2); // remplace circle(300, 300, 100);
}`}
        </CodeBlock>
      </CorrectionBox>
      </Note>


      <Note type="tip" title="💡 Pourquoi multiplier par 2 dans circle() ?">
        Parce que p5.js demande le diamètre, or nous avons le rayon. Donc rayon × 2.
      </Note>

      

      <Separator type="text" text="Détection du clic sur le cercle" color="gray-darker" size="md" />
      <SectionTitle>🖱️ 3/ Détection du clic sur le cercle</SectionTitle>

      <p>On veut maintenant détecter si le joueur clique sur le cercle.</p>
      <Note type="tip" >
      Il suffit de calculer la distance entre le clic souris et le centre du cercle.<br/>
      Si cette distance est inférieure au rayon du cercle, alors le joueur a cliqué sur le cercle!
      </Note>

      <p>Pour cela on va utiliser la fonction mathematique de p5.js dist qui calcule la distance entre 2 coordonnée :</p>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>dist(x1, y1, x2, y2)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Calcule la distance entre deux points. <br/>- Paramètre 1 : position x du premier point<br/> - Paramètre 2 : position y du premier point<br/> - Paramètre 3 : position x du deuxième point<br/> - Paramètre 4 : position y du deuxième point<br/> - Retourne : la distance en pixels</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <Note type="tip" title="💡 Comment on fait pour calculer la distance entre 2 points ?">
      Pour les matheux — et ceux qui se rappellent de leurs cours de collège — la distance entre deux points dans un repère orthogonal est :<br/>
<Image 
          src={distImage} 
          alt="Système de coordonnées dans p5.js"
          borderRadius="xl"
          shadow={true}
          border={false}
          width="250px"
          className="mt-2"
        />
C'est exactement ce que fait la fonction dist() dans p5.js.
      </Note>

      <p>Alors x1 et y1, c'est facile : ce sont les coordonnées du cercle (<InlineCode>cercle_x, cercle_y</InlineCode>).<br/>
      Mais comment récupérer les coordonnées du clic souris ?<br/>
Rien de plus simple avec p5.js : les variables <InlineCode>mouseX et mouseY</InlineCode> contiennent ces coordonnées en temps réel. <br/>Dès que tu bouges la souris, mouseX et mouseY sont automatiquement mis à jour avec les bonnes valeurs.<br/>
      </p>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Variable</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
          <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>mouseX</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">position x de la souris</td>
          </tr>
          <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>mouseY</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">position y de la souris</td>
          </tr>

          </tbody>
        </table>
      </TableContainer>
      
      <Note type="exercice" title="🧪 Calcul de la distance :">
        Dans la fonction <InlineCode>mousePressed()</InlineCode> :<br/>
        - D'abord on calcule la distance entre le centre du cercle (cercle_x, cercle_y) et le clic souris (mouseX, mouseY) :
        <CodeBlock language="javascript">
{`let distance = dist(mouseX, mouseY, cercle_x, cercle_y);`}
        </CodeBlock>
        - Ensuite on compare cette distance au rayon du cercle. Si la distance est inférieure au rayon, alors on a cliqué dans le cercle !
        <CodeBlock language="javascript">
{`if (distance < cercle_rayon) {
  print("Clic dans le cercle !");
}`}
        </CodeBlock>

        - Enfin, on affiche la distance arrondi à l'entier le plus proche dans la console :
        <CodeBlock language="javascript">
{`print("Distance : " + round(distance));`}
        </CodeBlock>
        <br/>
        <Result>▶️ Clique et tu verras la distance entre la souris et le cercle. <br/>Si tu clique sur le cercle, alors on affiche un message.</Result>

        <CorrectionBox>
        <CodeBlock language="javascript" showCopyButton={true}>
{`function mousePressed() {
  let distance = dist(mouseX, mouseY, cercle_x, cercle_y);
  if (distance < cercle_rayon) {
    print("Clic dans le cercle !");
  }
  print("Distance : " + round(distance));
}`}
        </CodeBlock>
      </CorrectionBox>

      </Note>
      <p>On va maintenant changer la position du cercle à chaque clic réussi.</p>

      <Note type="exercice" title="🧪 Position aléatoire :">
        - Toujours dans <InlineCode>mousePressed()</InlineCode>, dans le if, change aléatoirement les variables cercle_x et cercle_y, comme on l'a fait dans le setup.
        <CodeBlock language="javascript">
{`cercle_x = random(50, 550);
cercle_y = random(50, 550);`}
        </CodeBlock>
        - Tu peux retirer toutes les fonctions <InlineCode>print</InlineCode>.


<br/>
      <br/>  <Result>▶️ Le cercle se déplace aléatoirement à chaque clic réussi!</Result>

      <CorrectionBox>
        <CodeBlock language="javascript" showCopyButton={true}>
{`function mousePressed() {
  let distance = dist(mouseX, mouseY, cercle_x, cercle_y);
  if (distance < cercle_rayon) {
    cercle_x = random(50, 550);
    cercle_y = random(50, 550);
  }
}`}
        </CodeBlock>
      </CorrectionBox>
      </Note>

      <Separator type="text" text="Factorisation" color="gray-darker" size="md" />
      <SectionTitle>🔧 4/ Factorisation</SectionTitle>

      <p>Ton code fonctionne bien, mais on peut le rendre plus propre.</p>
      <p>Tu remarques qu'on répète deux fois ce code : une fois dans le <InlineCode>setup()</InlineCode>, et une fois dans le <InlineCode>mousePressed()</InlineCode></p>

      <CodeBlock language="javascript">
{`cercle_x = random(50, 550);
cercle_y = random(50, 550);`}
      </CodeBlock>

      <p>
      On va factoriser ce morceau de code en créant une fonction  : <InlineCode>setCirclePosition</InlineCode><br/>
      Ça rendra le code plus lisible et plus facilement maintenable, car si tu veux changer le random, tu n'auras à le modifier qu'à un seul endroit.
      </p>

      <Note type="exercice" title="🧪 À toi de jouer :">
        - Crée la fonction <InlineCode>setCirclePosition</InlineCode> en bas de ton code :
      <CodeBlock language="javascript">
{`function setCirclePosition() {
  cercle_x = random(50, 550);
  cercle_y = random(50, 550);
}`}
      </CodeBlock>
        - Dans <InlineCode>setup()</InlineCode> et <InlineCode>mousePressed()</InlineCode>, remplace les deux lignes :
        <CodeBlock language="javascript">
          <ColoredText color="red">- cercle_x = random(50, 550);</ColoredText>{"\n"}
          <ColoredText color="red">- cercle_y = random(50, 550);</ColoredText>
          <p>par un simple appel :</p>
          <ColoredText color="green">+ setCirclePosition();</ColoredText>
        </CodeBlock>
        C'est plus lisible et plus facile à maintenir !
      </Note>

      <Separator type="text" text="Code final" color="gray-darker" size="md" />

      <Note type="tip" title="✅ Correction">
        Si tout s'est bien passé, tu devrais avoir un code du genre :
      </Note>

      <CodeBlock language="javascript">
{`// Jeu 1 : Améliore ta visée
let cercle_rayon = 50;
let cercle_x = 0;
let cercle_y = 0;

function setup() {
  createCanvas(600, 600);
  setCirclePosition();
}

function draw() {
  background(9, 26, 52);

  // Cercle
  fill(34, 174, 220);
  circle(cercle_x, cercle_y, cercle_rayon * 2);
}

function mousePressed() {
  let distance = dist(mouseX, mouseY, cercle_x, cercle_y);
  if (distance < cercle_rayon) {
    setCirclePosition();
  }
}

function setCirclePosition() {
  cercle_x = random(50, 550);
  cercle_y = random(50, 550);
}`}
      </CodeBlock>

      <Note type="info" title="🔜 Prochaine étape :">
        Ajouter un score et un timer !
      </Note>
   
    </>
  );
};

export default Chapitre2Content; 